import { useState } from 'react';
import {
  Briefcase,
  Building2,
  Disc3,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { extractErrorMessage } from '@/lib/api-client';
import {
  SUPPLIER_TYPE_LABELS,
  useDeleteSupplier,
  useSuppliersList,
  type Supplier,
  type SupplierType,
} from './api';
import { SupplierFormDialog } from './supplier-form-dialog';

const TYPE_ICONS: Record<SupplierType, React.ComponentType<{ className?: string }>> = {
  workshop: Wrench,
  parts_store: Briefcase,
  tire_shop: Disc3,
  other: Building2,
};

export function SuppliersPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Supplier | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const listQuery = useSuppliersList({
    page: 1,
    limit: 100,
    q: debouncedSearch || undefined,
    type: typeFilter === 'all' ? undefined : typeFilter,
    active: activeFilter === 'all' ? undefined : activeFilter,
  });
  const deleteSupplier = useDeleteSupplier();

  const items = listQuery.data?.data ?? [];
  const total = listQuery.data?.meta.total ?? 0;

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSupplier.mutateAsync(deleteTarget.id);
      toast.success(`Fornecedor "${deleteTarget.name}" removido`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Building2 className="size-3.5" /> Frota
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Fornecedores
          </h1>
          <p className="text-sm text-muted-foreground">
            Oficinas, lojas de peças, borracharias e outros prestadores que atendem sua frota.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Novo fornecedor
        </Button>
      </header>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CNPJ ou cidade..."
              className="pl-9"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {(Object.keys(SUPPLIER_TYPE_LABELS) as SupplierType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {SUPPLIER_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={activeFilter}
              onValueChange={(v) => setActiveFilter(v as typeof activeFilter)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Ativos e inativos</SelectItem>
                <SelectItem value="true">Apenas ativos</SelectItem>
                <SelectItem value="false">Apenas inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-4">
          {listQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Building2 className="size-10 text-muted-foreground" />
              <p className="text-sm font-medium">
                {search || typeFilter !== 'all' || activeFilter !== 'all'
                  ? 'Nenhum fornecedor encontrado'
                  : 'Nenhum fornecedor cadastrado'}
              </p>
              <p className="text-xs text-muted-foreground">
                {search || typeFilter !== 'all' || activeFilter !== 'all'
                  ? 'Ajuste os filtros para ver outros resultados.'
                  : 'Clique em "Novo fornecedor" para cadastrar oficinas, lojas de peças e mais.'}
              </p>
              {!(search || typeFilter !== 'all' || activeFilter !== 'all') && (
                <Button onClick={() => setCreateOpen(true)} className="mt-2">
                  <Plus className="size-4" />
                  Novo fornecedor
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((s) => {
                const Icon = TYPE_ICONS[s.type] ?? Building2;
                return (
                  <Card key={s.id}>
                    <CardContent className="flex items-center justify-between gap-4 p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <Icon className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">{s.name}</p>
                          <p className="text-xs text-muted-foreground">
                            <Badge variant="secondary" className="mr-1.5">
                              {SUPPLIER_TYPE_LABELS[s.type]}
                            </Badge>
                            {s.cnpj && (
                              <span className="font-mono">{s.cnpj}</span>
                            )}
                            {s.city && s.state && (
                              <span>
                                {s.cnpj ? ' · ' : ''}
                                {s.city}/{s.state}
                              </span>
                            )}
                          </p>
                          {s.phone && (
                            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Phone className="size-3" />
                              <a
                                href={`tel:${s.phone}`}
                                className="hover:text-primary"
                              >
                                {s.phone}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.active ? (
                          <Badge variant="success">Ativo</Badge>
                        ) : (
                          <Badge variant="outline">Inativo</Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-8">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTarget(s)}>
                              <Pencil className="size-4" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(s)}
                              className="text-destructive"
                            >
                              <Trash2 className="size-4" /> Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {total > items.length && (
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  Mostrando {items.length} de {total} fornecedores
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <SupplierFormDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        supplier={editTarget}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover fornecedor</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong>?
              OS existentes que apontam para este fornecedor são preservadas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              loading={deleteSupplier.isPending}
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
